import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faBoxOpen, 
  faQuestionCircle, 
  faShieldAlt, 
  faEnvelope, 
  faRobot,
  faPlus,
  faTrash,
  faSave,
  faUndo,
  faArrowRight,
  faPhone,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { SuccessToastContainer, ErrorToastContainer } from '../../function/CustomFunction';
import { getToken } from '../../function/token';
import domain from '../../../reusecomponent/domain';
import Preloader from '../preloader';
import './ChatbotConfiguration.css';

export default function ChatbotConfiguration() {
  const [formData, setFormData] = useState({
    // Company Information
    company_name: '',
    company_about: '',
    company_mission: '',
    company_services: '',
    
    // Products
    products: [
      { name: '', price: '', description: '' }
    ],
    
    // FAQs
    faqs: [
      { question: '', answer: '' }
    ],
    
    // Policies
    refund_policy: '',
    warranty_policy: '',
    cancellation_policy: '',
    support_policy: '',
    
    // Contact Information
    email: [''],
    phone: [''],
    office_location: [''],
    whatsapp: [''],
    
    // Chatbot Settings
    chatbot_language: 'English',
    chatbot_tone: 'Formal',
    status: 'active'
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const steps = [
    { id: 'company', name: 'Company Info', icon: faBuilding },
    { id: 'products', name: 'Products', icon: faBoxOpen },
    { id: 'faqs', name: 'FAQs', icon: faQuestionCircle },
    { id: 'policies', name: 'Policies', icon: faShieldAlt },
    { id: 'contact', name: 'Contact Info', icon: faEnvelope },
    { id: 'chatbot', name: 'Chatbot Settings', icon: faRobot }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleObjectArrayChange = (field, index, key, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [key]: value } : item
      )
    }));
  };

  const addArrayItem = (field, defaultValue = '') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addObjectArrayItem = (field, defaultObject) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultObject]
    }));
  };

  const removeObjectArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      console.log('Submitting chatbot configuration:', formData);
      
      const success = await saveChatbotConfiguration(formData);
      if (success) {
        // Optionally reload the data to confirm it was saved
        await fetchChatbotConfiguration();
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      company_name: '',
      company_about: '',
      company_mission: '',
      company_services: '',
      products: [{ name: '', price: '', description: '' }],
      faqs: [{ question: '', answer: '' }],
      refund_policy: '',
      warranty_policy: '',
      cancellation_policy: '',
      support_policy: '',
      email: [''],
      phone: [''],
      office_location: [''],
      whatsapp: [''],
      chatbot_language: 'English',
      chatbot_tone: 'Formal',
      status: 'active'
    });
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  // API Functions
  const fetchChatbotConfiguration = async () => {
    try {
      const token = getToken();
      console.log("Domain: ",domain());
      
      const response = await fetch(`${domain()}/ai-chatbot`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      console.log('GET API Response:', result);

      if (response.ok && result.status) {
        // If data exists, populate the form
        if (result.data) {
          setFormData({
            company_name: result.data.company_name || '',
            company_about: result.data.company_about || '',
            company_mission: result.data.company_mission || '',
            company_services: result.data.company_services || '',
            products: result.data.products?.length > 0 ? result.data.products : [{ name: '', price: '', description: '' }],
            faqs: result.data.faqs?.length > 0 ? result.data.faqs : [{ question: '', answer: '' }],
            refund_policy: result.data.refund_policy || '',
            warranty_policy: result.data.warranty_policy || '',
            cancellation_policy: result.data.cancellation_policy || '',
            support_policy: result.data.support_policy || '',
            email: result.data.email?.length > 0 ? result.data.email : [''],
            phone: result.data.phone?.length > 0 ? result.data.phone : [''],
            office_location: result.data.office_location?.length > 0 ? result.data.office_location : [''],
            whatsapp: result.data.whatsapp?.length > 0 ? result.data.whatsapp : [''],
            chatbot_language: result.data.chatbot_language || 'English',
            chatbot_tone: result.data.chatbot_tone || 'Formal',
            status: result.data.status || 'active'
          });
        }
      } else {
        console.log('No existing configuration found or API returned error:', result.message);
      }
    } catch (error) {
      console.error('Error fetching chatbot configuration:', error);
      ErrorToastContainer('Failed to load chatbot configuration');
    } finally {
      setInitialLoading(false);
    }
  };

  const saveChatbotConfiguration = async (configData) => {
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/ai-chatbot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(configData)
      });

      const result = await response.json();
      console.log('POST API Response:', result);

      if (response.ok && result.status) {
        SuccessToastContainer('Chatbot configuration saved successfully!');
        return true;
      } else {
        ErrorToastContainer(result.message || 'Failed to save chatbot configuration');
        return false;
      }
    } catch (error) {
      console.error('Error saving chatbot configuration:', error);
      ErrorToastContainer('Failed to save chatbot configuration. Please try again.');
      return false;
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchChatbotConfiguration();
  }, []);

  const renderCompanySection = () => (
    <div className="form-section">
      <h3><FontAwesomeIcon icon={faBuilding} /> Company Information</h3>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>Company Name <span className="required">*</span></label>
            <input
              type="text"
              className="form-control"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              placeholder="Enter company name"
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>Company About <span className="required">*</span></label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.company_about}
              onChange={(e) => handleInputChange('company_about', e.target.value)}
              placeholder="Brief description about the company"
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>Mission Statement</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.company_mission}
              onChange={(e) => handleInputChange('company_mission', e.target.value)}
              placeholder="Company mission statement"
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>Services</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.company_services}
              onChange={(e) => handleInputChange('company_services', e.target.value)}
              placeholder="Description of services offered"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderProductsSection = () => (
    <div className="form-section">
      <h3><FontAwesomeIcon icon={faBoxOpen} /> Products</h3>
      {formData.products.map((product, index) => (
        <div key={index} className="product-item">
          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>Product Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={product.name}
                  onChange={(e) => handleObjectArrayChange('products', index, 'name', e.target.value)}
                  placeholder="Product name"
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Price <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={product.price}
                  onChange={(e) => handleObjectArrayChange('products', index, 'price', e.target.value)}
                  placeholder="Price"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={product.description}
                  onChange={(e) => handleObjectArrayChange('products', index, 'description', e.target.value)}
                  placeholder="Short description"
                />
              </div>
            </div>
            <div className="col-md-1">
              <button
                type="button"
                className="btn btn-danger btn-sm mt-4"
                onClick={() => removeObjectArrayItem('products', index)}
                disabled={formData.products.length === 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={() => addObjectArrayItem('products', { name: '', price: '', description: '' })}
      >
        <FontAwesomeIcon icon={faPlus} /> Add Product
      </button>
    </div>
  );

  const renderFAQsSection = () => (
    <div className="form-section">
      <h3><FontAwesomeIcon icon={faQuestionCircle} /> Frequently Asked Questions</h3>
      {formData.faqs.map((faq, index) => (
        <div key={index} className="faq-item">
          <div className="row">
            <div className="col-md-5">
              <div className="form-group">
                <label>Question <span className="required">*</span></label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={faq.question}
                  onChange={(e) => handleObjectArrayChange('faqs', index, 'question', e.target.value)}
                  placeholder="Enter question"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Answer <span className="required">*</span></label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={faq.answer}
                  onChange={(e) => handleObjectArrayChange('faqs', index, 'answer', e.target.value)}
                  placeholder="Enter answer"
                />
              </div>
            </div>
            <div className="col-md-1">
              <button
                type="button"
                className="btn btn-danger btn-sm mt-4"
                onClick={() => removeObjectArrayItem('faqs', index)}
                disabled={formData.faqs.length === 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={() => addObjectArrayItem('faqs', { question: '', answer: '' })}
      >
        <FontAwesomeIcon icon={faPlus} /> Add FAQ
      </button>
    </div>
  );

  const renderPoliciesSection = () => (
    <div className="form-section">
      <h3><FontAwesomeIcon icon={faShieldAlt} /> Policies</h3>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>Refund Policy</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.refund_policy}
              onChange={(e) => handleInputChange('refund_policy', e.target.value)}
              placeholder="Information on refund procedures"
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>Warranty Policy</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.warranty_policy}
              onChange={(e) => handleInputChange('warranty_policy', e.target.value)}
              placeholder="Warranty coverage and terms"
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>Cancellation Policy</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.cancellation_policy}
              onChange={(e) => handleInputChange('cancellation_policy', e.target.value)}
              placeholder="Cancellation terms"
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>Support Policy</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.support_policy}
              onChange={(e) => handleInputChange('support_policy', e.target.value)}
              placeholder="Customer support information"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderContactSection = () => (
    <div className="form-section">
      <h3><FontAwesomeIcon icon={faEnvelope} /> Contact Information</h3>
      
      {/* Email Addresses */}
      <div className="contact-group">
        <div className="contact-header">
          <h4><FontAwesomeIcon icon={faEnvelope} /> Email Addresses</h4>
          <button
            type="button"
            className="btn btn-primary btn-sm add-btn"
            onClick={() => addArrayItem('email', '')}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Email
          </button>
        </div>
        <div className="contact-items">
          {formData.email.map((email, index) => (
            <div key={index} className="contact-item">
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => handleArrayChange('email', index, e.target.value)}
                placeholder="Enter email address"
              />
              <button
                type="button"
                className="btn btn-remove"
                onClick={() => removeArrayItem('email', index)}
                disabled={formData.email.length === 1}
                title="Remove email"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Phone Numbers */}
      <div className="contact-group">
        <div className="contact-header">
          <h4><FontAwesomeIcon icon={faPhone} /> Phone Numbers</h4>
          <button
            type="button"
            className="btn btn-primary btn-sm add-btn"
            onClick={() => addArrayItem('phone', '')}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Phone
          </button>
        </div>
        <div className="contact-items">
          {formData.phone.map((phone, index) => (
            <div key={index} className="contact-item">
              <input
                type="tel"
                className="form-control"
                value={phone}
                onChange={(e) => handleArrayChange('phone', index, e.target.value)}
                placeholder="Enter phone number"
              />
              <button
                type="button"
                className="btn btn-remove"
                onClick={() => removeArrayItem('phone', index)}
                disabled={formData.phone.length === 1}
                title="Remove phone"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Office Locations */}
      <div className="contact-group">
        <div className="contact-header">
          <h4><FontAwesomeIcon icon={faMapMarkerAlt} /> Office Locations</h4>
          <button
            type="button"
            className="btn btn-primary btn-sm add-btn"
            onClick={() => addArrayItem('office_location', '')}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Location
          </button>
        </div>
        <div className="contact-items">
          {formData.office_location.map((location, index) => (
            <div key={index} className="contact-item">
              <input
                type="text"
                className="form-control"
                value={location}
                onChange={(e) => handleArrayChange('office_location', index, e.target.value)}
                placeholder="Enter office location"
              />
              <button
                type="button"
                className="btn btn-remove"
                onClick={() => removeArrayItem('office_location', index)}
                disabled={formData.office_location.length === 1}
                title="Remove location"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* WhatsApp Contacts */}
      <div className="contact-group">
        <div className="contact-header">
          <h4><FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Contacts</h4>
          <button
            type="button"
            className="btn btn-primary btn-sm add-btn"
            onClick={() => addArrayItem('whatsapp', '')}
          >
            <FontAwesomeIcon icon={faPlus} /> Add WhatsApp
          </button>
        </div>
        <div className="contact-items">
          {formData.whatsapp.map((whatsapp, index) => (
            <div key={index} className="contact-item">
              <input
                type="tel"
                className="form-control"
                value={whatsapp}
                onChange={(e) => handleArrayChange('whatsapp', index, e.target.value)}
                placeholder="Enter WhatsApp number"
              />
              <button
                type="button"
                className="btn btn-remove"
                onClick={() => removeArrayItem('whatsapp', index)}
                disabled={formData.whatsapp.length === 1}
                title="Remove WhatsApp"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderChatbotSection = () => (
    <div className="form-section">
      <h3><FontAwesomeIcon icon={faRobot} /> Chatbot Settings</h3>
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>Language <span className="required">*</span></label>
            <select
              className="form-control"
              value={formData.chatbot_language}
              onChange={(e) => handleInputChange('chatbot_language', e.target.value)}
            >
              <option value="English">English</option>
              <option value="Hindi">Hindi</option>
              <option value="Bengali">Bengali</option>
              <option value="Tamil">Tamil</option>
              <option value="Telugu">Telugu</option>
            </select>
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Tone <span className="required">*</span></label>
            <select
              className="form-control"
              value={formData.chatbot_tone}
              onChange={(e) => handleInputChange('chatbot_tone', e.target.value)}
            >
              <option value="Formal">Formal</option>
              <option value="Informal">Informal</option>
              <option value="Humorous">Humorous</option>
              <option value="Professional">Professional</option>
              <option value="Friendly">Friendly</option>
            </select>
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>Status <span className="required">*</span></label>
            <select
              className="form-control"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentSection = () => {
    const currentSection = steps[currentStep].id;
    switch (currentSection) {
      case 'company': return renderCompanySection();
      case 'products': return renderProductsSection();
      case 'faqs': return renderFAQsSection();
      case 'policies': return renderPoliciesSection();
      case 'contact': return renderContactSection();
      case 'chatbot': return renderChatbotSection();
      default: return renderCompanySection();
    }
  };

  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Show loading spinner while fetching initial data
  if (initialLoading) {
    return <Preloader action={initialLoading} />;
  }

  return (
    <div className="chatbot-configuration">
      <div className="container-fluid">
        <div className="config-wrapper">
          {/* Progress Steps */}
          <div className="steps-header">
            <div className="steps-container">
              {steps.map((step, index) => (
                <div 
                  key={step.id} 
                  className={`step-item ${index <= currentStep ? 'completed' : ''} ${index === currentStep ? 'active' : ''}`}
                  onClick={() => handleStepClick(index)}
                >
                  <div className="step-circle">
                    <FontAwesomeIcon icon={step.icon} />
                  </div>
                  <div className="step-label">{step.name}</div>
                  {index < steps.length - 1 && <div className="step-connector"></div>}
                </div>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="config-content">
            <div className="config-header">
              <h2>
                <FontAwesomeIcon icon={steps[currentStep].icon} />
                {steps[currentStep].name}
              </h2>
              <p>Step {currentStep + 1} of {steps.length}</p>
            </div>

            <div className="form-container">
              {renderCurrentSection()}
            </div>

            {/* Navigation Buttons */}
            <div className="config-navigation">
              <div className="nav-left">
                {!isFirstStep && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePrevious}
                  >
                    Previous
                  </button>
                )}
              </div>
              
              <div className="nav-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleReset}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faUndo} /> Reset All
                </button>
              </div>

              <div className="nav-right">
                {!isLastStep ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNext}
                  >
                    Next <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-success btn-lg"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faSave} />
                    {loading ? ' Saving...' : ' Save Configuration'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}